import { AnimatePresence, motion } from 'framer-motion';
import React, { ReactElement, useRef } from 'react';
import { Column, SortingRule, TableInstance } from 'react-table';
import styled from 'styled-components';

import { colors } from '../../../core';
import Icon from '../Icon';
import ScrollingPanel from '../ScrollingPanel';
import Table from '../Table';

interface TableWithDetailsProps<T> {
  children: ReactElement;
  columns: Column[];
  columnsToShow: string[];
  controlledPageCount?: number;
  data: T[];
  detailsHeader?: ReactElement;
  hiddenColumns: string[];
  manualPagination?: boolean;
  manualSortBy?: boolean;
  numberOfRowsToShow?: number;
  onClickMergeButton?: (instance: TableInstance) => void;
  onCloseDetails?: () => void;
  onLoadMore?: (pageIndex?: number, pageSize?: number, sortBy?: SortingRule<object>[]) => void;
  onRowClick: (id: string) => void;
  removeFilters?: boolean;
  rowProps?: (row: any) => any;
  selectedRowId?: string;
  showDetails: boolean;
  showPagination?: boolean;
  showSelectColum?: boolean;
  suspendOnClickInThisColumns?: string[];
  zIndex?: string;
  autoResetPage?: boolean;
  scrollingPrimary?: boolean;
  scrollingDetails?: boolean;
}

interface DetailsContainerProps {
  zIndex?: string;
}

const TableFrame = styled.div`
  flex: 1;
  position: relative;
`;

const TableContainer = styled.div`
  position: relative;
  display: flex;
  flex: 1;
  overflow-x: clip;
  height: 100%;
`;

const DetailsContainer = styled(motion.div)<DetailsContainerProps>`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  height: 100%;
  background-color: ${colors.white};
  border-left: 1px solid ${colors.cloud};
  z-index: ${({ zIndex }) => (zIndex ? zIndex : 10)};
`;

export const Panel = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 30px;
  border-bottom: 2px solid ${colors.riverstone};
`;

const Details = ({
  openDetails = false,
  columnsToShow,
  children,
  zIndex,
}: {
  children: React.ReactNode;
  columnsToShow: string[];
  openDetails: boolean;
  zIndex?: string;
}) => {
  const detailsRef = useRef<null | HTMLDivElement>(null);
  const columns = Array.from(document.querySelectorAll('th'));

  const getColumnSize = () => {
    const columnSize = columns.reduce((size, item): number => {
      if (columnsToShow.includes(item.id as any)) {
        return size + item.clientWidth;
      }

      return size;
    }, 0);

    return columnSize;
  };

  const detailsStyles = {
    open: {
      width: `calc(100% - ${getColumnSize()}px)`,
      opacity: 1,
    },
    closed: {
      width: 0,
      opacity: 0,
    },
  };

  return (
    <AnimatePresence>
      <DetailsContainer
        ref={detailsRef}
        initial="closed"
        variants={detailsStyles}
        animate={openDetails ? 'open' : 'closed'}
        transition={{ type: 'spring', stiffness: 350, damping: 40 }}
        zIndex={zIndex}
      >
        {children}
      </DetailsContainer>
    </AnimatePresence>
  );
};

const TableWithDetails = <T extends {}>({
  columns,
  data,
  hiddenColumns = [],
  numberOfRowsToShow = 0,
  onRowClick,
  showPagination,
  selectedRowId,
  removeFilters = false,
  manualPagination = false,
  onLoadMore,
  controlledPageCount = 0,
  manualSortBy = false,
  columnsToShow = [],
  showDetails = false,
  children,
  showSelectColum,
  onClickMergeButton,
  rowProps,
  suspendOnClickInThisColumns,
  zIndex,
  onCloseDetails,
  detailsHeader,
  autoResetPage = false,
  scrollingPrimary = false,
  scrollingDetails = false,
}: TableWithDetailsProps<T>) => {
  return (
    <TableContainer>
      <TableFrame>
        {scrollingPrimary ? (
          <ScrollingPanel>
            <Table<T>
              data={data}
              columns={columns}
              hiddenColumns={hiddenColumns}
              numberOfRowsToShow={numberOfRowsToShow}
              onRowClick={onRowClick}
              removeFilters={removeFilters}
              showPagination={showPagination}
              selectedRowId={selectedRowId}
              onLoadMore={onLoadMore}
              controlledPageCount={controlledPageCount}
              manualPagination={manualPagination}
              manualSortBy={manualSortBy}
              showSelectColum={showSelectColum}
              onClickMergeButton={onClickMergeButton}
              rowProps={rowProps}
              suspendOnClickInThisColumns={suspendOnClickInThisColumns}
              autoResetPage={autoResetPage}
            />
          </ScrollingPanel>
        ) : (
          <Table<T>
            data={data}
            columns={columns}
            hiddenColumns={hiddenColumns}
            numberOfRowsToShow={numberOfRowsToShow}
            onRowClick={onRowClick}
            removeFilters={removeFilters}
            showPagination={showPagination}
            selectedRowId={selectedRowId}
            onLoadMore={onLoadMore}
            controlledPageCount={controlledPageCount}
            manualPagination={manualPagination}
            manualSortBy={manualSortBy}
            showSelectColum={showSelectColum}
            onClickMergeButton={onClickMergeButton}
            rowProps={rowProps}
            suspendOnClickInThisColumns={suspendOnClickInThisColumns}
            autoResetPage={autoResetPage}
          />
        )}
      </TableFrame>
      <Details openDetails={showDetails} columnsToShow={columnsToShow} zIndex={zIndex}>
        {onCloseDetails && (
          <Panel>
            <div>{detailsHeader ? detailsHeader : null}</div>
            <Icon type="caret-right" color={colors.primary} onClick={onCloseDetails} />
          </Panel>
        )}
        {scrollingDetails ? (
          <ScrollingPanel>{children}</ScrollingPanel>
        ) : (
          <React.Fragment>{children}</React.Fragment>
        )}
      </Details>
    </TableContainer>
  );
};

export default TableWithDetails;
